import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import PhotoCameraRounded from '@mui/icons-material/PhotoCameraRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import PageHeader from '../components/common/PageHeader';
import storeLabel from '../utils/storeLabel';
import MultiImageUploader from '../components/common/MultiImageUploader';
import Checkbox from '@mui/material/Checkbox';
import FileDownloadRounded from '@mui/icons-material/FileDownloadRounded';
import UploadFileRounded from '@mui/icons-material/UploadFileRounded';
import { useStores } from '../context/StoresContext';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import {
  getInstallations, getInstallation, createInstallation, updateInstallation,
  deleteInstallation, updateInstallationProducts, importInstallations, exportInstallationsCsv,
  bulkUpdateInstallationStoreStatus, bulkDeleteInstallations,
  pushInstallationToShopify, searchShopifyForInstallation, matchInstallation, unmatchInstallation, updateInstallationShopifyStatus,
} from '../services/installationsApi';
import { getProducts } from '../services/productsApi';

const Installations = () => {
  const { activeStores } = useStores();
  const [items, setItems] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formName, setFormName] = useState('');
  const [formLocation, setFormLocation] = useState('');
  const [formDesigner, setFormDesigner] = useState('');
  const [formWebsite, setFormWebsite] = useState('');
  const [formText, setFormText] = useState('');
  const [formProducts, setFormProducts] = useState([]);
  const [formImages, setFormImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Shopify matching
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [matchStoreId, setMatchStoreId] = useState(null);
  const [matchInstId, setMatchInstId] = useState(null);
  const [matchQuery, setMatchQuery] = useState('');
  const [matchResults, setMatchResults] = useState([]);
  const [matchSearching, setMatchSearching] = useState(false);
  const [matchingProduct, setMatchingProduct] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [pushing, setPushing] = useState(null);

  // Bulk selection
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkStore, setBulkStore] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const toggleSelection = (id) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    const ids = items.map((i) => i.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedItems.has(id));
    setSelectedItems(allSelected ? new Set() : new Set(ids));
  };
  const handleBulkPublish = async () => {
    if (!bulkStore || selectedItems.size === 0) return;
    setBulkProcessing(true);
    try {
      const ids = Array.from(selectedItems);
      for (const instId of ids) {
        const item = items.find((i) => i.id === instId);
        const link = (item?.store_statuses || []).find((s) => s.store_id === bulkStore.id);
        if (link?.shopify_product_id) {
          // Already linked — just update status to active
          await updateInstallationShopifyStatus(instId, bulkStore.id, 'active');
        } else {
          // Not linked — push to Shopify
          await pushInstallationToShopify(instId, bulkStore.id);
        }
      }
      setSelectedItems(new Set());
      await fetchData();
    } catch (err) { console.error('Bulk publish failed:', err); }
    finally { setBulkProcessing(false); }
  };
  const handleBulkUnpublish = async () => {
    if (!bulkStore || selectedItems.size === 0) return;
    setBulkProcessing(true);
    try {
      await bulkUpdateInstallationStoreStatus(Array.from(selectedItems), bulkStore.id, 'inactive');
      setSelectedItems(new Set());
      await fetchData();
    } catch (err) { console.error('Bulk unpublish failed:', err); }
    finally { setBulkProcessing(false); }
  };
  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    if (!window.confirm(`Delete ${selectedItems.size} selected installation(s)? This cannot be undone.`)) return;
    setBulkProcessing(true);
    try {
      await bulkDeleteInstallations(Array.from(selectedItems));
      setSelectedItems(new Set());
      await fetchData();
    } catch (err) { console.error('Bulk delete failed:', err); }
    finally { setBulkProcessing(false); }
  };

  // Shopify push handler - creates or updates a product in Shopify. When an
  // existing match is found, the user confirms the override first.
  const handlePush = async (instId, storeId) => {
    const inst = items.find((i) => i.id === instId);
    const link = (inst?.store_statuses || []).find((s) => s.store_id === storeId);
    if (link?.shopify_product_id) {
      const store = activeStores.find((s) => s.id === storeId);
      if (!window.confirm(
        `"${inst.name}" is already live on ${storeLabel(store) || 'this store'} (Shopify product ${link.shopify_product_id}).\n\n` +
        `Pushing will OVERRIDE the existing Shopify product. Continue?`
      )) return;
    }
    setPushing(`${instId}-${storeId}`);
    try {
      const data = await pushInstallationToShopify(instId, storeId);
      await fetchData();
      const errs = data?.metafield_errors || [];
      if (errs.length > 0) {
        alert(`Pushed with ${errs.length} metafield warning(s):\n` + errs.map((e) => `  ${e.key || 'field'}: ${e.message}`).join('\n'));
      }
    } catch (err) {
      console.error('Push failed:', err);
      const data = err.response?.data;
      const lines = ['Push failed: ' + (data?.message || err.message)];
      if (data?.detail) lines.push('Detail: ' + (typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail)).slice(0, 400));
      if (data?.log_id) lines.push(`See Logs page (log #${data.log_id}) for details.`);
      alert(lines.join('\n'));
    }
    finally { setPushing(null); }
  };

  // Shopify matching handlers
  const openMatch = (instId, storeId) => {
    setMatchInstId(instId);
    setMatchStoreId(storeId);
    setMatchQuery('');
    setMatchResults([]);
    setMatchDialogOpen(true);
  };
  const handleMatchSearch = async () => {
    if (!matchQuery.trim() || !matchStoreId) return;
    setMatchSearching(true);
    try {
      const data = await searchShopifyForInstallation(matchStoreId, matchQuery);
      setMatchResults(data.products || []);
    } catch (err) { console.error('Search failed:', err); }
    finally { setMatchSearching(false); }
  };
  const handleMatch = async (shopifyProductId) => {
    setMatchingProduct(true);
    try {
      await matchInstallation(matchInstId, matchStoreId, shopifyProductId);
      setMatchDialogOpen(false);
      await fetchData();
    } catch (err) { console.error('Match failed:', err); }
    finally { setMatchingProduct(false); }
  };
  const handleUnmatch = async (instId, storeId) => {
    if (!window.confirm('Unlink this installation from this store?')) return;
    try {
      await unmatchInstallation(instId, storeId);
      await fetchData();
    } catch (err) { console.error('Unmatch failed:', err); }
  };
  const handleShopifyStatusChange = async (instId, storeId, newStatus) => {
    setUpdatingStatus(`${instId}-${storeId}`);
    try {
      await updateInstallationShopifyStatus(instId, storeId, newStatus);
      await fetchData();
    } catch (err) { console.error('Status change failed:', err); }
    finally { setUpdatingStatus(null); }
  };

  const fetchData = useCallback(async () => {
    try {
      const [instData, prodData] = await Promise.all([
        getInstallations(),
        getProducts(1, 100),
      ]);
      setItems(instData.items);
      setAllProducts(prodData.products);
    } catch (err) {
      console.error('Failed to fetch installations:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditItem(null);
    setFormName(''); setFormLocation(''); setFormDesigner(''); setFormWebsite(''); setFormText(''); setFormImages([]);
    setFormProducts([]);
    setError('');
    setDialogOpen(true);
  };

  const openEdit = async (item) => {
    try {
      const data = await getInstallation(item.id);
      const inst = data.item;
      setEditItem(inst);
      setFormName(inst.name);
      setFormLocation(inst.location || '');
      setFormDesigner(inst.designer || '');
      setFormWebsite(inst.website_url || '');
      setFormText(inst.text || '');
      setFormImages(Array.isArray(inst.images) ? inst.images.filter(Boolean) : []);
      setFormProducts(inst.products.map((p) => p.id));
      setError('');
      setDialogOpen(true);
    } catch (err) { console.error('Failed to load installation:', err); }
  };

  const handleSave = async () => {
    if (!formName.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const params = {
        name: formName.trim(),
        location: formLocation.trim(),
        designer: formDesigner.trim(),
        website_url: formWebsite.trim(),
        text: formText,
        images: (formImages || []).filter(Boolean),
      };
      if (editItem) {
        await updateInstallation(editItem.id, params);
        await updateInstallationProducts(editItem.id, formProducts);
      } else {
        const data = await createInstallation(params);
        if (formProducts.length > 0) {
          await updateInstallationProducts(data.item.id, formProducts);
        }
      }
      setDialogOpen(false);
      await fetchData();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.message;
      setError(msg || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this installation?')) return;
    setDeleting(id);
    try { await deleteInstallation(id); await fetchData(); }
    catch (err) { console.error('Failed to delete:', err); }
    finally { setDeleting(null); }
  };

  if (loading) {
    return (
      <Box>
        <PageHeader title="Installations" subtitle="Manage project installations" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </Box>
    );
  }

  return (
    <Box>
      <input
        id="installations-import-file"
        type="file"
        accept=".csv,.tsv,text/csv,text/tab-separated-values"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          setImporting(true);
          try {
            const text = await file.text();
            // Parse CSV properly handling quoted fields with embedded newlines and commas
            const parseCSV = (csvText) => {
              const rows = [];
              let row = [];
              let field = '';
              let inQuotes = false;
              for (let i = 0; i < csvText.length; i++) {
                const ch = csvText[i];
                if (inQuotes) {
                  if (ch === '"') {
                    if (i + 1 < csvText.length && csvText[i + 1] === '"') {
                      field += '"'; i++;
                    } else {
                      inQuotes = false;
                    }
                  } else {
                    field += ch;
                  }
                } else {
                  if (ch === '"') {
                    inQuotes = true;
                  } else if (ch === ',') {
                    row.push(field.trim()); field = '';
                  } else if (ch === '\t') {
                    row.push(field.trim()); field = '';
                  } else if (ch === '\n' || ch === '\r') {
                    if (ch === '\r' && csvText[i + 1] === '\n') i++;
                    row.push(field.trim()); field = '';
                    if (row.some((c) => c !== '')) rows.push(row);
                    row = [];
                  } else {
                    field += ch;
                  }
                }
              }
              row.push(field.trim());
              if (row.some((c) => c !== '')) rows.push(row);
              return rows;
            };
            const allRows = parseCSV(text);
            if (allRows.length < 2) throw new Error('No data rows');
            // Find header row: look for a row starting with "_id"
            let headerIdx = 0;
            for (let i = 0; i < Math.min(allRows.length, 5); i++) {
              const firstCell = (allRows[i][0] || '').toLowerCase();
              if (firstCell === '_id') {
                headerIdx = i; break;
              }
            }
            const headers = allRows[headerIdx].map((h) => h.toLowerCase().trim());
            console.log('Installations CSV - Header row index:', headerIdx, 'Headers:', headers);
            const items = [];
            for (let i = headerIdx + 1; i < allRows.length; i++) {
              const obj = {};
              headers.forEach((h, idx) => { obj[h] = (allRows[i][idx] || '').trim(); });
              items.push(obj);
            }
            console.log('Installations CSV - First item:', items[0], 'Total items:', items.length);
            const result = await importInstallations(items);
            const r = result.results;
            const parts = [];
            if (r.created) parts.push(`${r.created} new installation${r.created !== 1 ? 's' : ''}`);
            if (r.updated) parts.push(`${r.updated} updated`);
            if (r.products_linked) parts.push(`${r.products_linked} product${r.products_linked !== 1 ? 's' : ''} linked`);
            if (r.skipped) parts.push(`${r.skipped} skipped`);
            alert(parts.length > 0 ? `Import complete! ${parts.join(', ')}` : 'Import complete! All items already up to date.');
            await fetchData();
          } catch (err) {
            console.error('Import failed', err);
            alert(`Import failed: ${err.response?.data?.message || err.message || 'Unknown error'}`);
          } finally {
            setImporting(false);
            e.target.value = '';
          }
        }}
      />
      <PageHeader
        title="Installations"
        subtitle="Manage project installations"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<FileDownloadRounded />} onClick={async () => {
              const res = await exportInstallationsCsv();
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = 'installations.csv'; a.click();
              URL.revokeObjectURL(url);
            }}>
              Export CSV
            </Button>
            <Button variant="outlined" startIcon={<UploadFileRounded />}
              onClick={() => document.getElementById('installations-import-file')?.click()}
              disabled={importing}>
              {importing ? 'Importing...' : 'Import CSV'}
            </Button>
            <Button variant="contained" startIcon={<AddRounded />} onClick={openCreate}>
              Add Installation
            </Button>
          </Box>
        }
      />

      {items.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <PhotoCameraRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No installations added</Typography>
            <Typography variant="body2" color="text.secondary">Click "Add Installation" to create your first entry</Typography>
          </Box>
        </Card>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search installations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ width: 300 }}
            />
          </Box>
          {selectedItems.size > 0 && (
            <Card sx={{ mb: 2, px: 2, py: 1.5, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {selectedItems.size} installation{selectedItems.size !== 1 ? 's' : ''} selected
                </Typography>
                <Autocomplete
                  size="small"
                  options={activeStores}
                  getOptionLabel={(o) => storeLabel(o)}
                  value={bulkStore}
                  onChange={(_, val) => setBulkStore(val)}
                  renderInput={(params) => <TextField {...params} label="Target Store" />}
                  sx={{ minWidth: 180 }}
                />
                <Button size="small" variant="contained" onClick={handleBulkPublish}
                  disabled={!bulkStore || bulkProcessing}>Publish</Button>
                <Button size="small" variant="outlined" onClick={handleBulkUnpublish}
                  disabled={!bulkStore || bulkProcessing}>Unpublish</Button>
                <Button size="small" onClick={() => setSelectedItems(new Set())}>Clear</Button>
                <Button size="small" variant="contained" color="error"
                  startIcon={<DeleteOutlineRounded />}
                  onClick={handleBulkDelete}
                  disabled={bulkProcessing}>Delete Selected</Button>
              </Box>
            </Card>
          )}
          <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 42 }}>
                    <Checkbox size="small"
                      checked={items.length > 0 && items.every((i) => selectedItems.has(i.id))}
                      indeterminate={items.some((i) => selectedItems.has(i.id)) && !items.every((i) => selectedItems.has(i.id))}
                      onChange={toggleSelectAll} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: 60 }}>Image</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Designer</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Products</TableCell>
                  {activeStores.map((store) => (
                    <TableCell key={store.id} sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 140 }}>
                      {storeLabel(store)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', width: 100 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.filter((item) => {
                  if (!searchQuery.trim()) return true;
                  const q = searchQuery.toLowerCase();
                  return item.name.toLowerCase().includes(q) ||
                    (item.location || '').toLowerCase().includes(q) ||
                    (item.designer || '').toLowerCase().includes(q);
                }).map((item) => (
                  <TableRow key={item.id} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selectedItems.has(item.id)} onChange={() => toggleSelection(item.id)} />
                    </TableCell>
                    <TableCell>
                      {Array.isArray(item.images) && item.images.length > 0 ? (
                        <Box component="img" src={item.images[0]} alt={item.name} sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }} />
                      ) : (
                        <Box sx={{ width: 40, height: 40, bgcolor: 'grey.100', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <PhotoCameraRounded sx={{ fontSize: 18, color: 'grey.400' }} />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{item.name}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{item.location || '--'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{item.designer || '--'}</Typography></TableCell>
                    <TableCell><Chip label={item.product_count} size="small" /></TableCell>
                    {activeStores.map((store) => {
                      const link = (item.store_statuses || []).find((s) => s.store_id === store.id);
                      const isLinked = !!link?.shopify_product_id;
                      const shopifyStatus = link?.shopify_status || 'unknown';
                      return (
                        <TableCell key={store.id}>
                          {isLinked ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <FormControl size="small" sx={{ minWidth: 100 }}>
                                <Select
                                  value={shopifyStatus !== 'unknown' ? shopifyStatus : ''}
                                  onChange={(e) => handleShopifyStatusChange(item.id, store.id, e.target.value)}
                                  displayEmpty
                                  disabled={updatingStatus === `${item.id}-${store.id}`}
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  <MenuItem value="" disabled><em>Status</em></MenuItem>
                                  <MenuItem value="active">Active</MenuItem>
                                  <MenuItem value="draft">Draft</MenuItem>
                                  <MenuItem value="archived">Archived</MenuItem>
                                </Select>
                              </FormControl>
                              <Button size="small" variant="outlined" onClick={() => handlePush(item.id, store.id)}
                                disabled={pushing === `${item.id}-${store.id}`}
                                sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
                                {pushing === `${item.id}-${store.id}` ? <CircularProgress size={14} color="inherit" /> : 'Update'}
                              </Button>
                              <Button size="small" variant="text" color="error" onClick={() => handleUnmatch(item.id, store.id)} sx={{ fontSize: '0.65rem', textTransform: 'none', p: 0, minWidth: 0 }}>
                                Unlink
                              </Button>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Button size="small" variant="contained" onClick={() => handlePush(item.id, store.id)}
                                disabled={pushing === `${item.id}-${store.id}`}
                                sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
                                {pushing === `${item.id}-${store.id}` ? <CircularProgress size={14} color="inherit" /> : 'Push'}
                              </Button>
                              <Button size="small" variant="text" onClick={() => openMatch(item.id, store.id)}
                                sx={{ fontSize: '0.6rem', textTransform: 'none', p: 0, minWidth: 0, color: 'text.secondary' }}>
                                Link existing
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: 'primary.main' }}>
                            <EditRounded fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(item.id)} disabled={deleting === item.id} sx={{ color: 'error.main' }}>
                            {deleting === item.id ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineRounded fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editItem ? 'Edit Installation' : 'Add Installation'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus label="Name" value={formName}
            onChange={(e) => { setFormName(e.target.value); setError(''); }}
            fullWidth sx={{ mt: 1, mb: 2 }} error={!!error} helperText={error} />
          <TextField label="Location" value={formLocation}
            onChange={(e) => setFormLocation(e.target.value)}
            fullWidth sx={{ mb: 2 }} placeholder="e.g. New York, NY" />
          <TextField label="Designer" value={formDesigner}
            onChange={(e) => setFormDesigner(e.target.value)}
            fullWidth sx={{ mb: 2 }} placeholder="e.g. Studio ABC" />
          <TextField label="Website URL" value={formWebsite}
            onChange={(e) => setFormWebsite(e.target.value)}
            fullWidth sx={{ mb: 2 }} placeholder="https://..." />
          <TextField label="Text" value={formText}
            onChange={(e) => setFormText(e.target.value)}
            fullWidth multiline minRows={3} sx={{ mb: 2 }}
            helperText="Pushed to Shopify as custom.installation_text." />
          <Box sx={{ mb: 2 }}>
            <MultiImageUploader value={formImages} onChange={setFormImages} folder="installations" label="Images" />
          </Box>
          <Autocomplete multiple options={allProducts} getOptionLabel={(o) => o.title}
            value={allProducts.filter((p) => formProducts.includes(p.id))}
            onChange={(_, val) => setFormProducts(val.map((v) => v.id))}
            renderInput={(params) => <TextField {...params} label="Products" />} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (editItem ? 'Save' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shopify Match Dialog */}
      <Dialog open={matchDialogOpen} onClose={() => setMatchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Push to Shopify</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search for an existing Shopify product to link this installation to.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              label="Search by title"
              value={matchQuery}
              onChange={(e) => setMatchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleMatchSearch()}
              fullWidth
            />
            <Button variant="contained" onClick={handleMatchSearch} disabled={matchSearching || !matchQuery.trim()} sx={{ minWidth: 100 }}>
              {matchSearching ? <CircularProgress size={20} color="inherit" /> : 'Search'}
            </Button>
          </Box>
          {matchResults.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {matchResults.map((sp) => (
                <Box key={sp.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  {sp.image && <Box component="img" src={sp.image} alt={sp.title} sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }} />}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{sp.title}</Typography>
                    <Typography variant="caption" color="text.secondary">ID: {sp.id} {sp.status ? `— ${sp.status}` : ''}</Typography>
                  </Box>
                  <Button size="small" variant="contained" onClick={() => handleMatch(sp.id)} disabled={matchingProduct}>
                    {matchingProduct ? <CircularProgress size={16} color="inherit" /> : 'Link'}
                  </Button>
                </Box>
              ))}
            </Box>
          )}
          {matchResults.length === 0 && matchQuery && !matchSearching && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No results. Try a different search term.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setMatchDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Installations;
